/**
 * Meta Pixel client-side helpers + server-side mirror via /api/meta/track.
 *
 * Each call:
 *  1. Fires `fbq('track', EventName, payload, { eventID })` in the browser
 *  2. Forwards the same payload + event_id to the backend so we can ALSO
 *     send it via Conversions API (deduped via event_id)
 *
 * Generates a UUID v4 per action — Meta dedupes browser & server events that
 * share the same `event_id`.
 *
 * No-op when fbq isn't loaded yet (adblockers etc). Never throws.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const fbqSafe = (...args) => {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    window.fbq(...args);
  } catch (err) {
    console.debug('[meta-pixel] fbq failed:', err?.message);
  }
};

// Read fbp + fbc cookies (Meta's first-party browser cookies).
// fbp = browser ID set automatically by Pixel
// fbc = click ID set when user came from Facebook ad (fbclid in URL)
const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? match[1] : null;
};

const getMetaCookies = () => ({
  fbp: readCookie('_fbp'),
  fbc: readCookie('_fbc'),
});

// Forward a mirrored event to backend (which will call Meta CAPI server-side).
const mirrorToServer = (eventName, eventId, body) => {
  try {
    const cookies = getMetaCookies();
    fetch(`${BACKEND_URL}/api/meta/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: typeof window !== 'undefined' ? window.location.href : null,
        fbp: cookies.fbp || undefined,
        fbc: cookies.fbc || undefined,
        ...body,
      }),
      keepalive: true,
    }).catch((err) => console.debug('[meta-pixel] mirror failed:', err?.message));
  } catch (err) {
    console.debug('[meta-pixel] mirror crash:', err?.message);
  }
};

const toItem = (item, idx = 0) => ({
  id: String(item.product_id || item.id || `unknown-${idx}`),
  quantity: Number(item.quantity) || 1,
  item_price: Number(item.price) || 0,
});

export const trackMetaPageView = () => {
  // Generate a unique event_id and use it for BOTH browser pixel and server CAPI
  // so Meta can dedupe them properly (otherwise CAPI events look like duplicates).
  const eventId = uuid();
  fbqSafe('track', 'PageView', {}, { eventID: eventId });
  mirrorToServer('PageView', eventId, {});
};

export const trackMetaViewContent = (product) => {
  const eventId = uuid();
  const contentId = String(product?.product_id || product?.id || 'unknown');
  const payload = {
    content_ids: [contentId],
    content_type: 'product',
    value: Number(product?.price) || 0,
    currency: 'SEK',
  };
  fbqSafe('track', 'ViewContent', payload, { eventID: eventId });
  mirrorToServer('ViewContent', eventId, payload);
};

export const trackMetaAddToCart = (item) => {
  const eventId = uuid();
  const value = (Number(item?.price) || 0) * (Number(item?.quantity) || 1);
  const contentId = String(item?.product_id || item?.id || 'unknown');
  const payload = {
    content_ids: [contentId],
    contents: [toItem(item)],
    content_type: 'product',
    value,
    currency: 'SEK',
  };
  fbqSafe('track', 'AddToCart', payload, { eventID: eventId });
  mirrorToServer('AddToCart', eventId, payload);
};

export const trackMetaInitiateCheckout = (cartItems, total) => {
  const eventId = uuid();
  const items = (cartItems || []).map(toItem);
  const payload = {
    content_ids: items.map((i) => i.id),
    contents: items,
    content_type: 'product',
    value: Number(total) || 0,
    currency: 'SEK',
    num_items: items.reduce((s, i) => s + i.quantity, 0),
  };
  fbqSafe('track', 'InitiateCheckout', payload, { eventID: eventId });
  mirrorToServer('InitiateCheckout', eventId, payload);
};

export const trackMetaPurchase = (order) => {
  if (!order) return;
  const eventId = order.order_id || order.session_id || uuid();
  const items = (order.items || []).map(toItem);
  const payload = {
    content_ids: items.map((i) => i.id),
    contents: items,
    content_type: 'product',
    value: Number(order.total_amount || order.total) || 0,
    currency: 'SEK',
    num_items: items.reduce((s, i) => s + i.quantity, 0),
    email: order.email || order.customer_email || undefined,
  };
  fbqSafe('track', 'Purchase', payload, { eventID: eventId });
  mirrorToServer('Purchase', eventId, payload);
};

export const trackMetaSearch = (searchQuery) => {
  if (!searchQuery) return;
  const eventId = uuid();
  const payload = { search_string: String(searchQuery).slice(0, 100), currency: 'SEK' };
  fbqSafe('track', 'Search', payload, { eventID: eventId });
  mirrorToServer('Search', eventId, payload);
};
